import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { get, post, ApiError } from '@/lib/api';
import { useSession } from '@/lib/auth';
import {
  type AnySub, type AnyOrder,
  productQuantityRows, tierUnits, trackingDisplay, latestOrderUnits, orderDocUnits,
} from '@/lib/ordering';
import { tiersFromSubmission, productOfTier } from '@/lib/prize-tiers';

type DetailsPayload = AnySub & { orders: AnyOrder[] };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-4">
      <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wide text-subtle">{title}</div>
      {children}
    </div>
  );
}

function TierQuantityCard({ tier, sub, orders, productName }: { tier: number; sub: AnySub; orders: AnyOrder[]; productName: string }) {
  const quantities = productQuantityRows(sub, orders, tier);
  const units = tierUnits(sub, orders, tier);
  return (
    <div className="rounded-md border border-border bg-bg/40 p-2.5">
      <div className="mb-1.5 flex items-center gap-1.5">
        <span className={`rounded-sm px-1.5 py-0.5 text-[9px] font-bold text-white ${tier === 1 ? 'bg-primary' : tier === 2 ? 'bg-[#8b5cf6]' : 'bg-text'}`}>P{tier}</span>
        <span className="truncate text-[12px] font-medium">{productName}</span>
      </div>
      <div className="flex flex-wrap gap-1">
        {quantities.length ? quantities.map((row) => (
          <span key={`${row.size}:${row.sku || ''}`} className="rounded-sm bg-primary/10 px-1.5 py-0.5 text-[11px]">
            <strong>{row.size}</strong>: {row.quantity}
            {row.sku ? <span className="ml-1 font-mono text-[9.5px] text-muted">{row.sku}</span> : null}
          </span>
        )) : units > 0 ? (
          <span className="text-[11px] text-muted">{units} units</span>
        ) : (
          <span className="text-[11px] text-subtle">No variant data</span>
        )}
      </div>
      <div className="mt-1.5 text-[11px] text-muted">Total: <strong className="text-text">{units} units</strong></div>
    </div>
  );
}

// Order details for one ordered submission: Shopify order info, per-tier
// quantities, shipping, the orders subcollection, and the sync-tracking /
// cancel actions. Port of admin-dashboard's openOrderDetails modal.
export function OrderDetailsDrawer({
  id,
  onClose,
  onChanged,
  productNames,
}: {
  id: string | null;
  onClose: () => void;
  onChanged: () => void;
  productNames: (pid?: string) => string;
}) {
  const { data, isLoading, mutate } = useSWR<DetailsPayload>(id ? `/api/order-details/${id}` : null, get);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // The API returns the subcollection orderBy createdAt DESC — index 0 is
  // the newest order. (admin-dashboard reads [length-1] against the same
  // sort and shows the oldest; deliberate fix, not parity.) The lib helpers
  // (tierQuantities/tierUnits) treat the LAST element as latest — the legacy
  // parent-array convention — so they get an ASC copy.
  const orders = data?.orders || [];
  const latest = orders.length ? orders[0] : null;
  const ascOrders = [...orders].reverse();

  // Multi-source fallbacks, same as admin-dashboard index.html:9455-9480.
  const shipTo = (data?.shippingAddress && (data.shippingAddress.address1 || data.shippingAddress.name))
    ? data.shippingAddress
    : (latest?.shippingAddress || data?.shippingAddress || data?.shipping || {});
  const shopifyOrderName = data?.shopifyOrderName || latest?.orderNumber || data?.lastOrderNumber || '-';
  const shopifyOrderUrl = data?.shopifyOrderUrl || latest?.shopifyOrderUrl || null;
  const orderedAt = data?.orderedAt || latest?.createdAt || data?.lastOrderAt || null;
  const customerEmail = data?.email || latest?.customer?.email || shipTo.email || '-';
  const tracking = data ? trackingDisplay(data) : null;
  // Port of admin-dashboard index.html:9311-9316 — no cancelling an order
  // that is already moving/delivered (Shopify would refuse the cancel but the
  // submission would still be reset into the ordering queue). 'unfulfilled'
  // already encodes the stale-reorder rule via trackingDisplay.
  const { user } = useSession();
  const canChangeOrder = user?.canChangePlacedOrders === true;
  const canCancel = canChangeOrder && tracking?.kind === 'unfulfilled';

  const syncTracking = async () => {
    if (!id) return;
    setSyncing(true);
    try {
      const r = await post<{ success: boolean; message?: string; error?: string }>(`/api/orders/${id}/sync-tracking`, {});
      toast.success(r.message || 'Tracking synced');
      await mutate();
      onChanged();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
      toast.error(body?.error || (e as Error).message || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const cancelOrder = async () => {
    if (!id || !data) return;
    const name = data.fundraiserName || id;
    if (!confirm(`Cancel the Shopify order for "${name}" and move it back to the Ordering queue?\n\nThis cancels a REAL order in Shopify (if one exists).`)) return;
    const reason = prompt('Reason for cancellation (optional):') ?? '';
    setCancelling(true);
    try {
      // The server answers 2xx only when orderwebhook confirmed BOTH the
      // Shopify cancel and the local reconciliation; anything else throws
      // into the catch below with nothing changed.
      const r = await post<{ success: boolean; message?: string; shopifyCancelled?: boolean; reconciled?: boolean }>(`/api/orders/${id}/cancel`, { reason });
      toast.success(r.message || 'Order cancelled and moved back to the ordering queue');
      onChanged();
      onClose();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { message?: string; error?: string } | null) : null;
      toast.error(body?.message || body?.error || (e as Error).message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Sheet open={!!id} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-[480px] overflow-y-auto sm:max-w-[480px]">
        <SheetHeader>
          <SheetTitle>{data?.fundraiserName || 'Order details'}</SheetTitle>
          <p className="font-mono text-[10.5px] text-subtle">{id}</p>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="mt-4 space-y-2">
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-6 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <div className="mt-4">
            <Section title="Order information">
              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-[12px]">
                <div className="text-muted">Admin</div><div>{data.adminName || '-'}</div>
                <div className="text-muted">Shopify order</div>
                <div>
                  {shopifyOrderUrl && shopifyOrderName !== '-' ? (
                    <a href={shopifyOrderUrl} target="_blank" rel="noreferrer" className="font-medium text-primary hover:underline">{shopifyOrderName} ↗</a>
                  ) : shopifyOrderName}
                </div>
                <div className="text-muted">Ordered</div>
                <div>{orderedAt ? new Date(orderedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '-'}</div>
                <div className="text-muted">Units (latest order)</div><div>{latest ? orderDocUnits(latest) : latestOrderUnits(data)}</div>
                <div className="text-muted">Customer</div><div className="truncate">{customerEmail}</div>
                <div className="text-muted">Tracking</div>
                <div>
                  {tracking && (
                    tracking.url ? (
                      <a href={tracking.url} target="_blank" rel="noreferrer"><Badge variant={tracking.kind === 'delivered' ? 'default' : 'secondary'}>{tracking.text}</Badge></a>
                    ) : (
                      <Badge variant="secondary">{tracking.text}</Badge>
                    )
                  )}
                  {tracking?.number && <div className="mt-0.5 font-mono text-[10px] text-subtle">{tracking.company ? `${tracking.company}: ` : ''}{tracking.number}</div>}
                </div>
              </div>
            </Section>

            <Section title="Products & quantities">
              <div className="space-y-2">
                {tiersFromSubmission(data).map((t) => (
                  <TierQuantityCard
                    key={t.index}
                    tier={t.index}
                    sub={data}
                    orders={ascOrders}
                    productName={productNames(productOfTier(data, t.index) || t.product || undefined)}
                  />
                ))}
              </div>
            </Section>

            <Section title="Shipping address">
              <div className="rounded-md border border-border bg-bg/40 p-2.5 text-[12px] leading-relaxed">
                <div>{[shipTo.firstName, shipTo.lastName].filter(Boolean).join(' ') || shipTo.name || '-'}</div>
                {shipTo.company && <div>{shipTo.company}</div>}
                {shipTo.address1 && <div>{shipTo.address1}</div>}
                {shipTo.address2 && <div>{shipTo.address2}</div>}
                <div>{[shipTo.city, shipTo.province || shipTo.state, shipTo.zip].filter(Boolean).join(', ')}</div>
                {shipTo.country && <div>{shipTo.country}</div>}
                {shipTo.phone && <div className="text-muted">{shipTo.phone}</div>}
              </div>
            </Section>

            {orders.length > 0 && (
              <Section title={`Orders (${orders.length})`}>
                <div className="space-y-1.5">
                  {orders.map((o) => (
                    <div key={o.id} className="flex items-center justify-between rounded-md border border-border bg-bg/40 px-2.5 py-1.5 text-[11.5px]">
                      <div>
                        <span className="font-medium">{o.orderNumber || o.id}</span>
                        <span className="ml-2 text-subtle">{o.createdAt ? new Date(o.createdAt).toLocaleDateString() : ''}</span>
                      </div>
                      <div className="text-muted">{orderDocUnits(o)} units</div>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <div className="mt-5 flex items-center gap-2 border-t border-border pt-3">
              <Button variant="secondary" size="sm" disabled={syncing} onClick={syncTracking}>
                {syncing ? 'Syncing…' : 'Sync tracking'}
              </Button>
              {canCancel ? (
                <Button variant="destructive" size="sm" disabled={cancelling} onClick={cancelOrder}>
                  {cancelling ? 'Cancelling…' : 'Cancel order'}
                </Button>
              ) : (
                <span className="text-[10.5px] text-subtle">{canChangeOrder ? 'Cancel unavailable — order has shipped or been delivered' : 'Placed orders are final. Only GhostFit can change them, and the rep pays for any change.'}</span>
              )}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
